import React, { useState } from 'react';
import INDIAN_STATES from '../../constants/indianStates';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Save, MapPin, Car, Users, Building2 } from 'lucide-react';
import { api } from '../../utils/api';

const ALL_SERVICES = [
  { key: 'auto',          label: 'Auto',          icon: '🛺' },
  { key: 'electric_auto', label: 'Electric Auto',  icon: '⚡' },
  { key: 'schedule_trip', label: 'Schedule',       icon: '📅' },
  { key: 'female_driver', label: 'Female Driver',  icon: '👩' },
  { key: 'night_trip',    label: 'Night Ride',     icon: '🌙' },
  { key: 'mygarage',      label: 'My Garage',      icon: '🏠' },
  { key: 'acting_driver', label: 'Acting Driver',  icon: '🚘' },
  { key: 'my_bookings',   label: 'My Bookings',    icon: '🧾' },
  { key: 'my_driver',     label: 'My Driver',      icon: '🧑‍✈️' },
];

/**
 * The region mode a human picks, mapped to the ServiceType values the backend stores.
 * Keeping the mapping in one place means the picker and the stored mix cannot drift.
 */
const MODE_SERVICES = {
  normal_only: ['RIDE'],
  acting_only: ['ACTING'],
  both: ['RIDE', 'ACTING'],
};

const DEFAULT_BANNERS = [
  { id: 'bannerStops',  key: 'bannerStops',  label: 'Multi Stop' },
  { id: 'bannerFamily', key: 'bannerFamily', label: 'Family Ride' },
  { id: 'bannerAuto',   key: 'bannerAuto',   label: 'Female Driver' },
];

const DEFAULT_ARRANGEMENTS = [
  { key: 'accommodation', label: 'Driver Accommodation', desc: 'Driver stay arrangements will be borne by the customer.', icon: 'bed',        iconLib: 'Ionicons',               enabled: true },
  { key: 'food',          label: 'Driver Food Allowance', desc: 'Food allowance for driver',                              icon: 'restaurant', iconLib: 'Ionicons',               enabled: true },
];

const DEFAULT_SPECIAL_REQUIREMENTS = [
  { key: 'kids',          label: 'Children on Board',      desc: '', icon: 'baby-carriage', iconLib: 'MaterialCommunityIcons', type: 'toggle', enabled: true },
  { key: 'elderly',       label: 'Elderly Passengers',     desc: '', icon: 'human-cane',    iconLib: 'MaterialCommunityIcons', type: 'toggle', enabled: true },
  { key: 'maxSpeed',      label: 'Comfort Speed',          desc: '', icon: 'speedometer',   iconLib: 'Ionicons',               type: 'number', enabled: true },
  { key: 'otherRequests', label: 'Custom Request / Notes', desc: '', icon: 'document-text', iconLib: 'Ionicons',               type: 'text',   enabled: true },
];

const REGION_MODES = [
  {
    key: 'normal_only',
    label: 'Normal Ride Only',
    icon: '🛺',
    description: 'Shows Auto, Electric Auto, Schedule, etc. Hides Acting Driver booking.',
    color: 'blue',
  },
  {
    key: 'acting_only',
    label: 'Acting Driver Only',
    icon: '🚘',
    description: 'Shows only Acting Driver booking. Hides all normal ride options.',
    color: 'purple',
  },
  {
    key: 'both',
    label: 'Both Services',
    icon: '✅',
    description: 'Shows all ride options including Acting Driver booking.',
    color: 'green',
  },
];

function buildHomeScreenConfig(mode) {
  let serviceKeys;
  if (mode === 'normal_only') {
    serviceKeys = ['auto', 'electric_auto', 'schedule_trip', 'female_driver', 'night_trip', 'mygarage'];
  } else if (mode === 'acting_only') {
    // An acting-only state still needs the surrounding journey, not just the booking
    // entry point: the customer's vehicles (My Garage), their trips (My Bookings) and
    // their saved drivers (My Drivers). Shipping only `acting_driver` left the home
    // screen with a single tile and no way back to anything already booked.
    serviceKeys = ['acting_driver', 'mygarage', 'my_bookings', 'my_driver'];
  } else {
    serviceKeys = ALL_SERVICES.map(s => s.key);
  }

  const serviceItems = serviceKeys.map(key => {
    const meta = ALL_SERVICES.find(s => s.key === key);
    return { key, label: meta?.label || key, bgColor: '#f3f3f3ff', textColor: '#858585ff', borderColor: '#ffffffff' };
  });

  return [
    { key: 'services', sectionTitle: 'Choose Your Ride', showSectionTitle: false, items: serviceItems },
    { key: 'horizontal_banners', sectionTitle: 'Offers', showSectionTitle: false, items: DEFAULT_BANNERS },
  ];
}

// Generate a short region code from the office name e.g. "Coimbatore" → "CBE"
function autoCode(name) {
  if (!name) return '';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return name.slice(0, 3).toUpperCase();
  return words.map(w => w[0]).join('').toUpperCase();
}

const modeColorMap = {
  blue:   { border: 'border-blue-400',   bg: 'bg-blue-50',   text: 'text-blue-700',   ring: 'ring-blue-400' },
  purple: { border: 'border-purple-400', bg: 'bg-purple-50', text: 'text-purple-700', ring: 'ring-purple-400' },
  green:  { border: 'border-green-400',  bg: 'bg-green-50',  text: 'text-green-700',  ring: 'ring-green-400' },
};

const CreateAppConfig = () => {
  const navigate = useNavigate();

  const [selectedOffice, setSelectedOffice] = useState(null);
  const [regionCode, setRegionCode] = useState('DEFAULT');
  const [regionCodeEdited, setRegionCodeEdited] = useState(false);
  const [stateName, setStateName] = useState('DEFAULT');
  const [regionMode, setRegionMode] = useState('');
  const [appType, setAppType] = useState('PUBLICRIDE_CUSTOMER_APP');
  const [formData, setFormData] = useState({
    APP_NAME: '',
    COMPANYNAME: '',
    COMPANYPHONE: '',
    COMPANYEMAIL: '',
    PAYMENT_METHODS: 'CASH,ONLINE',
  });

  // Fetch all regional offices
  const { data: officesData, isLoading: officesLoading } = useQuery(
    'allRegionalOffices',
    () => api.get('/regional-offices', { params: { limit: 100, page: 1 } }),
    {
      select: (res) => res?.data?.data?.offices || res?.data?.data || [],
      refetchOnWindowFocus: false,
    }
  );
  const offices = Array.isArray(officesData) ? officesData : [];

  const createMutation = useMutation(
    (data) => api.post('/publicRidesAppConfig', data),
    {
      onSuccess: (res) => {
        toast.success('App config created successfully');
        navigate(`/config/${res.data.data._id}`);
      },
      onError: (error) => {
        const msg = error?.response?.data?.message
          || error?.response?.data?.error
          || error?.message
          || 'Failed to create app config';
        toast.error(msg);
        console.error('Create config error:', error?.response?.data || error);
      }
    }
  );

  // The app resolves its config with GET /public?regionCode=… and matches this
  // string exactly, so it has to be what the app sends. Derive a code from the
  // state by default; 'DEFAULT' stays the fallback served to any app whose region
  // code has no config of its own (including older builds that send none).
  const codeForState = (name) =>
    !name || name === 'DEFAULT'
      ? 'DEFAULT'
      : name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_');

  const handleStateChange = (name) => {
    setStateName(name);
    if (!regionCodeEdited) setRegionCode(codeForState(name));
  };

  const handleOfficeSelect = (office) => {
    setSelectedOffice(office);
    const code = autoCode(office.name);
    setRegionCode(code);
    if (!formData.APP_NAME) {
      setFormData(prev => ({ ...prev, APP_NAME: `App - ${office.name}` }));
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isCustomerApp = appType === 'PUBLICRIDE_CUSTOMER_APP' || appType === 'CUSTOMER';

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!stateName) {
      toast.error('Please select a state');
      return;
    }
    if (!regionCode.trim()) {
      toast.error('Please enter a region code');
      return;
    }
    if (isCustomerApp && !regionMode) {
      toast.error('Please select a region mode');
      return;
    }
    if (!formData.APP_NAME.trim()) {
      toast.error('Please enter an app name');
      return;
    }

    const isActingEnabled = isCustomerApp && (regionMode === 'acting_only' || regionMode === 'both');

    const payload = {
      APP: appType,
      ...formData,
      // regionCode is what /public matches on, so it is the field that actually
      // scopes this config to a region. state is stored alongside it for display.
      regionCode: regionCode.trim() || 'DEFAULT',
      state: stateName || 'DEFAULT',
      HOME_SCREEN_CONFIG: isCustomerApp ? buildHomeScreenConfig(regionMode) : [],
      // The explicit product mix for this state. Stated here rather than left to be
      // inferred from the home-screen layout — a business rule read from a UI array is
      // one that a layout change can silently break. The server derives
      // `actingDriverEnabled` from this, so the two can never disagree.
      enabledServices: isCustomerApp ? MODE_SERVICES[regionMode] || ['RIDE'] : ['RIDE'],
      actingDriverEnabled: isActingEnabled,
      ACTING_DRIVER_ARRANGEMENTS: isActingEnabled ? DEFAULT_ARRANGEMENTS : [],
      ACTING_DRIVER_SPECIAL_REQUIREMENTS: isActingEnabled ? DEFAULT_SPECIAL_REQUIREMENTS : [],
    };

    createMutation.mutate(payload);
  };

  const stepNum = (n) => isCustomerApp ? n : n - 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate('/config')} className="text-gray-600 hover:text-gray-900">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="page-title">Create App Config</h1>
              <p className="page-subtitle">Set up a region-specific app configuration</p>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Step 1: App Type */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title flex items-center gap-2"><Users className="h-4 w-4" /> Step 1 — App Type</h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { value: 'PUBLICRIDE_CUSTOMER_APP', label: 'Customer App', icon: '👤', desc: 'Passenger-facing app' },
                { value: 'DRIVER',                  label: 'Driver App',   icon: '🚗', desc: 'Driver-facing app' },
              ].map(opt => {
                const selected = appType === opt.value;
                return (
                  <div
                    key={opt.value}
                    onClick={() => setAppType(opt.value)}
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all select-none
                      ${selected ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                  >
                    <span className="text-3xl">{opt.icon}</span>
                    <div className="flex-1">
                      <p className={`font-semibold ${selected ? 'text-primary-700' : 'text-gray-800'}`}>{opt.label}</p>
                      <p className="text-xs text-gray-500">{opt.desc}</p>
                    </div>
                    {selected && <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center"><span className="text-white text-xs">✓</span></div>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Step 2: Regional Office */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title flex items-center gap-2"><MapPin className="h-4 w-4" /> Step 2 — Select State</h3>
          </div>
          <div className="card-body">
            <p className="text-xs text-gray-500 mb-3">
              This config applies to users in the selected state (resolved from their location).
              Choose <span className="font-semibold">DEFAULT</span> to make it the fallback for every state without its own config.
            </p>
            {/* State — the identifier for this config. */}
            <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3">
              <div>
                <p className="text-xs font-semibold text-gray-600">State</p>
                <p className="text-xs text-gray-400">The app serves this config to users in this state (DEFAULT = fallback for everyone else).</p>
              </div>
              <select
                value={stateName}
                onChange={e => handleStateChange(e.target.value)}
                className="sm:ml-auto min-w-[12rem] px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="DEFAULT">DEFAULT (fallback)</option>
                {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {/* Region code — the value the app actually looks the config up by. */}
            <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3">
              <div>
                <p className="text-xs font-semibold text-gray-600">Region Code</p>
                <p className="text-xs text-gray-400">
                  The app requests its config with this exact code, so it must match what the app sends.
                  Keep <span className="font-semibold">DEFAULT</span> for the fallback config.
                </p>
              </div>
              <input
                type="text"
                value={regionCode}
                onChange={e => { setRegionCode(e.target.value); setRegionCodeEdited(true); }}
                placeholder="e.g. TAMIL_NADU"
                className="sm:ml-auto min-w-[12rem] px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            {regionCode.trim().toUpperCase() === 'DEFAULT' && (
              <p className="mt-2 text-xs text-amber-700">
                This will be the fallback config — every app whose region code has no config of its
                own receives it, including older builds that send no region code at all.
              </p>
            )}
          </div>
        </div>

        {/* Step 3: Region Mode (customer app only) */}
        {isCustomerApp && (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title flex items-center gap-2"><Car className="h-4 w-4" /> Step 3 — What services will this region offer?</h3>
            </div>
            <div className="card-body">
              <p className="text-sm text-gray-500 mb-4">
                Controls which ride options appear on the customer home screen for <strong>{selectedOffice?.name || 'this region'}</strong>.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {REGION_MODES.map(mode => {
                  const selected = regionMode === mode.key;
                  const colors = modeColorMap[mode.color];
                  return (
                    <div
                      key={mode.key}
                      onClick={() => setRegionMode(mode.key)}
                      className={`flex flex-col gap-2 p-4 rounded-xl border-2 cursor-pointer transition-all select-none
                        ${selected ? `${colors.border} ${colors.bg} ring-2 ${colors.ring}` : 'border-gray-200 bg-white hover:border-gray-300'}`}
                    >
                      <span className="text-3xl">{mode.icon}</span>
                      <p className={`font-semibold text-sm ${selected ? colors.text : 'text-gray-800'}`}>{mode.label}</p>
                      <p className="text-xs text-gray-500">{mode.description}</p>
                      {selected && <span className={`text-xs font-medium ${colors.text}`}>✓ Selected</span>}
                    </div>
                  );
                })}
              </div>

              {regionMode && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs font-semibold text-gray-600 mb-2">Services visible in this region:</p>
                  <div className="flex flex-wrap gap-2">
                    {buildHomeScreenConfig(regionMode)[0].items.map(item => {
                      const meta = ALL_SERVICES.find(s => s.key === item.key);
                      return (
                        <span key={item.key} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-300 rounded-full text-xs text-gray-700">
                          {meta?.icon} {item.label}
                        </span>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Acting Driver booking: <strong>{(regionMode === 'acting_only' || regionMode === 'both') ? '✅ Enabled' : '❌ Disabled'}</strong>
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 4 (or 3 for driver): Basic Info */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Step {isCustomerApp ? '4' : '3'} — Basic Info</h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">App Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.APP_NAME}
                  onChange={e => handleChange('APP_NAME', e.target.value)}
                  placeholder={selectedOffice ? `e.g. MyApp - ${selectedOffice.name}` : 'e.g. MyApp - Coimbatore'}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <input
                  type="text"
                  value={formData.COMPANYNAME}
                  onChange={e => handleChange('COMPANYNAME', e.target.value)}
                  placeholder="e.g. VirtualMaze Technologies"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Phone</label>
                <input
                  type="text"
                  value={formData.COMPANYPHONE}
                  onChange={e => handleChange('COMPANYPHONE', e.target.value)}
                  placeholder="+91 9876543210"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Email</label>
                <input
                  type="email"
                  value={formData.COMPANYEMAIL}
                  onChange={e => handleChange('COMPANYEMAIL', e.target.value)}
                  placeholder="info@company.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Methods</label>
                <input
                  type="text"
                  value={formData.PAYMENT_METHODS}
                  onChange={e => handleChange('PAYMENT_METHODS', e.target.value)}
                  placeholder="CASH,ONLINE"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-gray-400 mt-1">Comma-separated: CASH, ONLINE, WALLET</p>
              </div>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-end gap-3 pb-6">
          <button type="button" onClick={() => navigate('/config')} className="btn btn-outline">
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isLoading}
            className="btn btn-primary flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            {createMutation.isLoading ? 'Creating...' : 'Create Config'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateAppConfig;
